/**
 * Hook for generating showroom buildings using a web worker.
 *
 * The worker fetches `/public-plans` itself on `initialize` and holds the marshalled Doc
 * privately. The main thread sends full option-choice snapshots after plan sync derives them.
 *
 * **Single call site invariant**: this hook is invoked exclusively from
 * `ShowroomGenerationRuntimeProvider`. Calling it from a second mount point would send a
 * second `initialize` message to the singleton worker, doubling the `/public-plans` fetch
 * and the render-payload pipeline.
 */
import { captureException } from "@sentry/react";
import type { ClientBuilderPlanGltfTimingSpan } from "api-types/builders/buildersResponses";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { ShowroomFloorPlanPayload } from "building-types/building/showroomFloorPlanPayloadTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import shallowCompare from "common/utils/shallowCompare";
import { getShowroomWorkerBuildingGenEnabledFromSearch } from "common/utils/showroomWorkerBuildingGen";
import stableHash from "common/utils/stableHash";
import type { BuildingRenderPayload } from "gfx/buildingRenderPayload";
import {
  recordShowroomIntroBenchmarkPhaseFromTiming,
  recordShowroomIntroBenchmarkPhaseToNow,
} from "lib/components/debug/showroomDebugHomeFile";
import { prefetchShowroomPropAssets } from "lib/components/showroom2/building/showroomPropPrefetch";
import {
  publishShowroomMaterialWarmup,
  resetShowroomMaterialWarmup,
} from "lib/components/showroom2/materials/showroomMaterialWarmup";
import { reconcileShowroomOptionCatalogSnapshot } from "lib/components/showroom2/options/showroomOptionCatalogSnapshot";
import {
  measureActiveOptionSelectionStage,
  optionSelectionPerformance,
} from "lib/components/showroom2/performance/optionSelectionPerformance";
import { showroomPerformance } from "lib/components/showroom2/performance/showroomPerformance";
import type {
  ShowroomOptionCatalogConfig,
  ShowroomOptionCatalogFingerprints,
  ShowroomOptionCatalogSnapshot,
} from "lib/components/showroom2/state/showroomTypes";
import {
  SHOWROOM_WORKER_HEARTBEAT_INTERVAL_MS,
  SHOWROOM_WORKER_HEARTBEAT_TIMEOUT_MS,
  showroomBuildingWorkerErrorDetailsFromUnknown,
  type MainToShowroomBuildingWorkerMessage,
  type PlanIdentifiers,
  type ShowroomOptionStateProtocol,
  type ShowroomBuildingWorkerError,
  type ShowroomBuildingWorkerErrorPhase,
  type ShowroomBuildingWorkerErrorDetails,
  type ShowroomWorkerFailurePhase,
  type ShowroomBuildingWorkerToMainMessage,
  type ShowroomWorkerBuildingTiming as SourceShowroomWorkerBuildingTiming,
} from "lib/workers/showroomBuildingWorkerMessages";
import {
  discardShowroomWorker,
  getShowroomWorkerSingletonState,
  recordTerminalShowroomWorkerFailure,
  registerShowroomWorker,
  setShowroomWorkerObserved,
  type ShowroomWorkerFailureMetadata,
  type TerminalShowroomWorkerFailure,
} from "lib/workers/showroomWorkerSingleton";
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";

interface ShowroomWorkerFailure extends ShowroomBuildingWorkerErrorDetails {
  filename?: string;
  lineno?: number;
  colno?: number;
}

type ShowroomWorkerPostMessageFailurePhase = `${MainToShowroomBuildingWorkerMessage["type"]}PostMessage`;

interface ShowroomWorkerWatchdogActivity {
  readonly sessionId: string;
  readonly requestId: number;
  readonly phase: ShowroomBuildingWorkerErrorPhase;
}

interface HandleTerminalFailureOptions {
  readonly worker?: Worker | null;
  readonly sessionId?: string | null;
  readonly metadata?: ShowroomWorkerFailureMetadata;
  readonly extra?: Record<string, unknown>;
}

const SHOWROOM_WORKER_HEARTBEAT_GRACE_MS = SHOWROOM_WORKER_HEARTBEAT_INTERVAL_MS * 2;
let nextShowroomSessionCounter = 0;
let nextShowroomRequestCounter = 0;

function generateSessionId(): string {
  nextShowroomSessionCounter += 1;
  return `showroom-session-${nextShowroomSessionCounter}-${Date.now()}`;
}

function getOrCreateShowroomWorker(): Worker | null {
  if (typeof window === "undefined") {
    return null;
  }

  const state = getShowroomWorkerSingletonState();
  if (state.failure) {
    return null;
  }
  if (state.worker) {
    return state.worker;
  }

  try {
    const worker = new Worker(new URL("lib/workers/showroomBuilding.worker.ts", import.meta.url), { type: "module" });
    return registerShowroomWorker(worker);
  } catch (error: unknown) {
    console.error("Failed to create showroom worker", error);
    recordTerminalShowroomWorkerFailure(error, "startup", undefined, {
      fallbackMessage: "Failed to start showroom worker",
    });
    return null;
  }
}

function generateRequestId(): number {
  nextShowroomRequestCounter += 1;
  return nextShowroomRequestCounter;
}

function errorFromWorkerFailure(error: ShowroomWorkerFailure): Error {
  const capturedError = new Error(error.message);
  capturedError.name = error.name;
  if (error.stack) {
    capturedError.stack = error.stack;
  }
  return capturedError;
}

function captureShowroomWorkerError(
  error: Error,
  {
    phase,
    fatal,
    sessionId,
    extra,
  }: {
    phase: ShowroomWorkerFailurePhase;
    fatal: boolean;
    sessionId?: string | null;
    extra?: Record<string, unknown>;
  },
): void {
  captureException(error, {
    tags: {
      "showroom.worker": "building",
      "showroom.worker.phase": phase,
      "showroom.worker.fatal": String(fatal),
    },
    extra: {
      sessionId: sessionId ?? undefined,
      ...extra,
    },
  });
}

function failureFromTerminalRecord(record: TerminalShowroomWorkerFailure): ShowroomWorkerFailure {
  return {
    ...showroomBuildingWorkerErrorDetailsFromUnknown(record.error, record.fallbackMessage),
    filename: record.filename,
    lineno: record.lineno,
    colno: record.colno,
  };
}
function shouldWarmShowroomWorker(defaultEnabled?: boolean): boolean {
  if (typeof window === "undefined") {
    return getShowroomWorkerBuildingGenEnabledFromSearch("", defaultEnabled);
  }

  return getShowroomWorkerBuildingGenEnabledFromSearch(window.location.search, defaultEnabled);
}

/**
 * Eagerly create the shared showroom worker so its script begins fetching and
 * booting before the consumer hook subscribes. Safe to call multiple times.
 */
export function initializeShowroomWorker({
  force = false,
  defaultEnabled,
}: { readonly force?: boolean; readonly defaultEnabled?: boolean } = {}): void {
  if (!force && !shouldWarmShowroomWorker(defaultEnabled)) {
    return;
  }

  getOrCreateShowroomWorker();
}

function identifiersKey(identifiers: PlanIdentifiers): string {
  return `${identifiers.builderId}|${identifiers.planType}|${identifiers.planId}|${identifiers.lotId ?? ""}`;
}

function planDataKey(planData: ClientHomePlanBaseData): string {
  return `plan-data|${planData.builder.id}|${planData.homePlan.id}|${planData.homePlan.studioDocId}|${planData.homePlan.updatedAt}`;
}

function enabledFeaturesKey(enabledFeatures: GenerateBuildingFeatureSet): string {
  return stableHash(enabledFeatures);
}

function recordClientBenchmarkPhase(name: string, startTimeMs: number | null, endTimeMs: number): void {
  recordShowroomIntroBenchmarkPhaseToNow(name, "client", startTimeMs, endTimeMs);
}

function recordWorkerSourceTiming(timing: SourceShowroomWorkerBuildingTiming): void {
  // Optional spans (materialParamsResolution, sourceResponseTotal) are skipped when absent.
  const spans: Array<[string, ClientBuilderPlanGltfTimingSpan | undefined]> = [
    ["worker.source.buildingGeneration", timing.buildingGeneration],
    ["worker.source.geometryCollection", timing.geometryCollection],
    ["worker.source.materialParamsResolution", timing.materialParamsResolution],
    ["worker.source.renderPayloadAssembly", timing.renderPayloadAssembly],
    ["worker.source.geometryHashing", timing.geometryHashing],
    ["worker.source.pipelineTotal", timing.sourcePipelineTotal],
    ["worker.source.responseTotal", timing.sourceResponseTotal],
  ];
  for (const [name, span] of spans) {
    if (span) {
      recordShowroomIntroBenchmarkPhaseFromTiming({ name, source: "worker", ...span });
    }
  }
  for (const stage of timing.buildingGenerationStages) {
    recordShowroomIntroBenchmarkPhaseFromTiming({
      name: `worker.source.buildingGeneration.${stage.stageId}`,
      source: "worker",
      startTimeMs: stage.startTimeMs,
      durationMs: stage.durationMs,
    });
  }
}

export interface ShowroomWorkerBuildingResultTiming extends SourceShowroomWorkerBuildingTiming {
  clientObservedTotal: ClientBuilderPlanGltfTimingSpan;
}

export interface ShowroomWorkerBuildingResult {
  renderPayload: BuildingRenderPayload | null;
  requestedOptionChoices: OptionChoices | null;
  planData: ClientHomePlanBaseData | null;
  /** Precomputed sheet-geometry floor plan from the most recent worker reply. */
  floorPlan: ShowroomFloorPlanPayload | null;
  /** Whether floor-plan generation failed without invalidating the matching render payload. */
  floorPlanError: boolean;
  isGenerating: boolean;
  error: string | null;
  timing: ShowroomWorkerBuildingResultTiming | null;
  optionCatalogSnapshot: ShowroomOptionCatalogSnapshot | null;
  dispatchOptionIntent: (optionSetId: string, optionId: string) => ShowroomOptionIntentDispatchResult;
  reportRenderPayloadHydrationFailure: (error: unknown) => void;
}

export type ShowroomOptionIntentDispatchResult =
  | { readonly status: "posted"; readonly requestId: number }
  | { readonly status: "notReady" }
  | { readonly status: "failed"; readonly requestId?: number };

export interface ActiveShowroomOptionChoice {
  readonly optionSetId: string;
  readonly optionId: string;
}

/**
 * Hook that manages the singleton showroom building worker.
 *
 * @param identifiers - Plan identifiers (builderId, planType, planId, lotId).
 * @param optionChoices - Current option choices.
 * @param enabled - Whether worker mode is active.
 * @param enabledFeatures - Building-generation feature flags to match main-thread generation.
 * @param optionChoicesReady - Whether `optionChoices` has been derived from the initialized plan data.
 * @param planDataOverride - Optional debug plan data for worker runs that do not have route identifiers.
 * @param selectedReportingUnitId - Selected unit for unit-scoped precomputed floor-plan geometry.
 * @param activeOptionChoice - Choice from the current user action, used to scope overridable option rules.
 * @param optionStateProtocol - Worker option protocol. Direct intents require explicit `optionStateV2` initialization.
 * @param optionCatalogConfig - Showroom presentation inputs needed by worker-side catalog derivation.
 */
export function useShowroomWorkerBuilding(
  identifiers: PlanIdentifiers | undefined,
  optionChoices: OptionChoices,
  enabled: boolean,
  enabledFeatures: GenerateBuildingFeatureSet,
  optionChoicesReady: boolean = true,
  planDataOverride?: ClientHomePlanBaseData | null,
  selectedReportingUnitId?: string,
  activeOptionChoice?: ActiveShowroomOptionChoice,
  optionStateProtocol: ShowroomOptionStateProtocol = "snapshot",
  optionCatalogConfig?: ShowroomOptionCatalogConfig,
): ShowroomWorkerBuildingResult {
  const effectivelyEnabled = enabled && (!!identifiers || !!planDataOverride);

  const identifierKey = useMemo(() => (identifiers ? identifiersKey(identifiers) : null), [identifiers]);
  const featureKey = enabledFeaturesKey(enabledFeatures);
  const optionCatalogConfigKey = optionCatalogConfig ? JSON.stringify(optionCatalogConfig) : "none";
  const sourceKey = useMemo(() => {
    let key: string | null;
    if (identifierKey) {
      key = identifierKey;
    } else {
      key = planDataOverride ? planDataKey(planDataOverride) : null;
    }
    return key
      ? `${key}|features:${featureKey}|optionState:${optionStateProtocol}|catalog:${optionCatalogConfigKey}`
      : null;
  }, [featureKey, identifierKey, optionCatalogConfigKey, optionStateProtocol, planDataOverride]);

  const workerRef = useRef<Worker | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const latestWorkerRequestIdRef = useRef<number | null>(null);
  const workerWatchdogRef = useRef<ShowroomWorkerWatchdogActivity | null>(null);
  const workerWatchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const workerWatchdogRemainingMsRef = useRef(SHOWROOM_WORKER_HEARTBEAT_TIMEOUT_MS);
  const workerWatchdogStartedAtRef = useRef<number | null>(null);
  const workerWatchdogGraceAvailableRef = useRef(true);
  const latestSentChoicesRef = useRef<OptionChoices | null>(null);
  const latestSentSelectedReportingUnitIdRef = useRef<string | undefined>(undefined);
  const latestSentActiveOptionChoiceRef = useRef<ActiveShowroomOptionChoice | undefined>(undefined);
  const latestResolvedChoicesRef = useRef<OptionChoices | null>(null);
  const currentOptionChoicesRef = useRef(optionChoices);
  const currentSelectedReportingUnitIdRef = useRef(selectedReportingUnitId);
  const initializedOptionStateProtocolRef = useRef<ShowroomOptionStateProtocol | null>(null);
  const optionIntentReadyRef = useRef(false);
  const generationInFlightRef = useRef(false);
  const latestRequestWasOptionIntentRef = useRef(false);
  const pendingIntentReflectionRef = useRef<{
    readonly baseOptionChoices: OptionChoices;
    resolvedOptionChoices: OptionChoices | null;
  } | null>(null);
  const optionCatalogSnapshotRef = useRef<ShowroomOptionCatalogSnapshot | null>(null);
  const optionCatalogFingerprintsRef = useRef<ShowroomOptionCatalogFingerprints | null>(null);
  const lastIdentifierKeyRef = useRef<string | null>(null);
  const initializePostTimeRef = useRef<number | null>(null);
  const initializedReceivedTimeRef = useRef<number | null>(null);
  const generationStartTimeRef = useRef<number | null>(null);
  const [renderPayload, setRenderPayload] = useState<BuildingRenderPayload | null>(null);
  const [requestedOptionChoices, setRequestedOptionChoices] = useState<OptionChoices | null>(null);
  const [planData, setPlanData] = useState<ClientHomePlanBaseData | null>(null);
  const [floorPlan, setFloorPlan] = useState<ShowroomFloorPlanPayload | null>(null);
  const [floorPlanError, setFloorPlanError] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timing, setTiming] = useState<ShowroomWorkerBuildingResultTiming | null>(null);
  const [optionCatalogSnapshot, setOptionCatalogSnapshot] = useState<ShowroomOptionCatalogSnapshot | null>(null);
  currentOptionChoicesRef.current = optionChoices;
  currentSelectedReportingUnitIdRef.current = selectedReportingUnitId;

  const clearWorkerWatchdogTimer = useCallback((): void => {
    if (workerWatchdogTimerRef.current !== null) {
      clearTimeout(workerWatchdogTimerRef.current);
      workerWatchdogTimerRef.current = null;
    }
  }, []);

  const pauseWorkerWatchdog = useCallback((): void => {
    if (workerWatchdogStartedAtRef.current !== null) {
      workerWatchdogRemainingMsRef.current = Math.max(
        0,
        workerWatchdogRemainingMsRef.current - (performance.now() - workerWatchdogStartedAtRef.current),
      );
      workerWatchdogStartedAtRef.current = null;
    }
    clearWorkerWatchdogTimer();
  }, [clearWorkerWatchdogTimer]);

  const stopWorkerWatchdog = useCallback((): void => {
    clearWorkerWatchdogTimer();
    workerWatchdogRef.current = null;
    workerWatchdogRemainingMsRef.current = SHOWROOM_WORKER_HEARTBEAT_TIMEOUT_MS;
    workerWatchdogStartedAtRef.current = null;
    workerWatchdogGraceAvailableRef.current = true;
  }, [clearWorkerWatchdogTimer]);

  const handleTerminalFailure = useCallback(
    (error: unknown, phase: ShowroomWorkerFailurePhase, options: HandleTerminalFailureOptions = {}): void => {
      const { worker: failureWorker, sessionId, metadata, extra } = options;
      const terminalFailure = recordTerminalShowroomWorkerFailure(error, phase, sessionId, metadata);
      const failure = failureFromTerminalRecord(terminalFailure);
      if (!terminalFailure.reported) {
        terminalFailure.reported = true;
        captureShowroomWorkerError(errorFromWorkerFailure(failure), {
          phase: terminalFailure.phase,
          fatal: true,
          sessionId: terminalFailure.sessionId,
          extra: {
            filename: terminalFailure.filename,
            lineno: terminalFailure.lineno,
            colno: terminalFailure.colno,
            ...extra,
          },
        });
      }

      stopWorkerWatchdog();
      resetShowroomMaterialWarmup(terminalFailure.sessionId);
      sessionIdRef.current = null;
      latestWorkerRequestIdRef.current = null;
      initializedOptionStateProtocolRef.current = null;
      optionIntentReadyRef.current = false;
      generationInFlightRef.current = false;
      latestRequestWasOptionIntentRef.current = false;
      pendingIntentReflectionRef.current = null;
      optionCatalogSnapshotRef.current = null;
      optionCatalogFingerprintsRef.current = null;
      generationStartTimeRef.current = null;
      setRenderPayload(null);
      setRequestedOptionChoices(null);
      setFloorPlan(null);
      setFloorPlanError(false);
      setTiming(null);
      setOptionCatalogSnapshot(null);
      setIsGenerating(false);
      setError(failure.message);

      const worker = failureWorker ?? workerRef.current ?? getShowroomWorkerSingletonState().worker;
      workerRef.current = null;
      setShowroomWorkerObserved("detached");
      if (worker) {
        discardShowroomWorker(worker);
      }
    },
    [stopWorkerWatchdog],
  );

  const handleFloorPlanFailure = useCallback((error: ShowroomBuildingWorkerError, sessionId: string): void => {
    captureShowroomWorkerError(errorFromWorkerFailure(error), {
      phase: error.phase,
      fatal: false,
      sessionId,
    });
    setFloorPlanError(true);
  }, []);

  const reportRenderPayloadHydrationFailure = useCallback(
    (hydrationError: unknown): void => {
      console.error(
        "ShowroomWorkerBuilding: Render payload hydration error:",
        showroomBuildingWorkerErrorDetailsFromUnknown(hydrationError).message,
      );
      handleTerminalFailure(hydrationError, "renderPayloadHydration", {
        worker: workerRef.current,
        sessionId: sessionIdRef.current,
      });
    },
    [handleTerminalFailure],
  );

  const armWorkerWatchdog = useCallback(
    (
      activity: ShowroomWorkerWatchdogActivity,
      timeoutMs: number = SHOWROOM_WORKER_HEARTBEAT_TIMEOUT_MS,
      graceAvailable: boolean = true,
    ): void => {
      clearWorkerWatchdogTimer();
      workerWatchdogRef.current = activity;
      workerWatchdogRemainingMsRef.current = timeoutMs;
      workerWatchdogStartedAtRef.current = null;
      workerWatchdogGraceAvailableRef.current = graceAvailable;
      if (document.visibilityState === "hidden") {
        return;
      }

      function scheduleTimer(durationMs: number, canUseGrace: boolean): void {
        workerWatchdogRemainingMsRef.current = durationMs;
        workerWatchdogStartedAtRef.current = performance.now();
        workerWatchdogGraceAvailableRef.current = canUseGrace;
        workerWatchdogTimerRef.current = setTimeout(() => {
          workerWatchdogTimerRef.current = null;
          workerWatchdogStartedAtRef.current = null;
          if (canUseGrace) {
            // Main-thread timers can run before worker timers after system sleep. Require one
            // final heartbeat window before treating the silence as a worker failure.
            scheduleTimer(SHOWROOM_WORKER_HEARTBEAT_GRACE_MS, false);
            return;
          }

          workerWatchdogRef.current = null;
          const timeoutError = new Error("Showroom worker stopped responding");
          handleTerminalFailure(timeoutError, "heartbeatTimeout", {
            worker: workerRef.current,
            sessionId: activity.sessionId,
            extra: {
              requestId: activity.requestId,
              activePhase: activity.phase,
              timeoutMs: SHOWROOM_WORKER_HEARTBEAT_TIMEOUT_MS,
              graceMs: SHOWROOM_WORKER_HEARTBEAT_GRACE_MS,
            },
          });
        }, durationMs);
      }

      scheduleTimer(timeoutMs, graceAvailable);
    },
    [clearWorkerWatchdogTimer, handleTerminalFailure],
  );

  const refreshWorkerWatchdog = useCallback(
    (requestId: number, phase?: ShowroomBuildingWorkerErrorPhase): void => {
      const activeActivity = workerWatchdogRef.current;
      if (!activeActivity || activeActivity.requestId !== requestId) {
        return;
      }
      armWorkerWatchdog({
        ...activeActivity,
        phase: phase ?? activeActivity.phase,
      });
    },
    [armWorkerWatchdog],
  );

  useEffect(() => {
    if (!effectivelyEnabled) {
      return;
    }

    const handleVisibilityChange = (): void => {
      const activeActivity = workerWatchdogRef.current;
      if (!activeActivity) {
        return;
      }
      if (document.visibilityState === "hidden") {
        pauseWorkerWatchdog();
      } else {
        armWorkerWatchdog(
          activeActivity,
          Math.max(workerWatchdogRemainingMsRef.current, SHOWROOM_WORKER_HEARTBEAT_GRACE_MS),
          workerWatchdogGraceAvailableRef.current,
        );
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [armWorkerWatchdog, effectivelyEnabled, pauseWorkerWatchdog]);

  const postMessageToWorker = useCallback(
    (
      worker: Worker,
      message: MainToShowroomBuildingWorkerMessage,
      phase: ShowroomWorkerPostMessageFailurePhase,
    ): boolean => {
      try {
        worker.postMessage(message);
        return true;
      } catch (postMessageError: unknown) {
        handleTerminalFailure(postMessageError, phase, { worker, sessionId: message.sessionId });
        console.error(
          "ShowroomWorkerBuilding: Failed to post worker message:",
          showroomBuildingWorkerErrorDetailsFromUnknown(postMessageError).message,
        );
        return false;
      }
    },
    [handleTerminalFailure],
  );

  const sendUpdateOptions = useCallback(
    (
      choices: OptionChoices,
      selectedReportingUnitLabelId: string | undefined,
      activeChoice: ActiveShowroomOptionChoice | undefined,
    ): void => {
      const sessionId = sessionIdRef.current;
      const worker = workerRef.current;
      if (getShowroomWorkerSingletonState().failure || !sessionId || !worker) {
        return;
      }

      const message: MainToShowroomBuildingWorkerMessage = {
        type: "updateOptions",
        sessionId,
        requestId: generateRequestId(),
        optionChoices: choices,
        activeChoices: activeChoice ? { [activeChoice.optionSetId]: activeChoice.optionId } : undefined,
        selectedReportingUnitLabelId,
      };
      const generationStartTime = performance.now();
      const posted = measureActiveOptionSelectionStage("workerPostMessage", () =>
        postMessageToWorker(worker, message, "updateOptionsPostMessage"),
      );
      optionSelectionPerformance((perf) => {
        const cycleId = perf.activeCycle?.id;
        if (cycleId !== undefined) {
          perf.end(cycleId, "optionClickToWorkerPost");
        }
      });
      if (!posted) {
        return;
      }

      latestWorkerRequestIdRef.current = message.requestId;
      latestSentChoicesRef.current = choices;
      latestSentSelectedReportingUnitIdRef.current = selectedReportingUnitLabelId;
      latestSentActiveOptionChoiceRef.current = activeChoice;
      latestRequestWasOptionIntentRef.current = false;
      pendingIntentReflectionRef.current = null;
      generationInFlightRef.current = true;
      generationStartTimeRef.current = generationStartTime;
      resetShowroomMaterialWarmup(sessionId);
      setFloorPlanError(false);
      setIsGenerating(true);
      setError(null);
      armWorkerWatchdog({
        sessionId: message.sessionId,
        requestId: message.requestId,
        phase: "updateOptions",
      });
    },
    [armWorkerWatchdog, postMessageToWorker],
  );

  const dispatchOptionIntent = useCallback(
    (optionSetId: string, optionId: string): ShowroomOptionIntentDispatchResult => {
      const sessionId = sessionIdRef.current;
      const worker = workerRef.current;
      if (
        getShowroomWorkerSingletonState().failure ||
        !sessionId ||
        !worker ||
        initializedOptionStateProtocolRef.current !== "optionStateV2" ||
        !optionIntentReadyRef.current ||
        !latestResolvedChoicesRef.current ||
        generationInFlightRef.current
      ) {
        return { status: "notReady" };
      }

      const requestId = generateRequestId();
      const message: MainToShowroomBuildingWorkerMessage = {
        type: "applyOptionChoice",
        sessionId,
        requestId,
        optionSetId,
        optionId,
        selectedReportingUnitLabelId: currentSelectedReportingUnitIdRef.current,
      };
      const generationStartTime = performance.now();
      const posted = measureActiveOptionSelectionStage("workerPostMessage", () =>
        postMessageToWorker(worker, message, "applyOptionChoicePostMessage"),
      );
      optionSelectionPerformance((perf) => {
        const cycleId = perf.activeCycle?.id;
        if (cycleId !== undefined) {
          perf.end(cycleId, "optionClickToWorkerPost");
        }
      });
      if (!posted) {
        return { status: "failed", requestId };
      }

      latestWorkerRequestIdRef.current = requestId;
      latestSentChoicesRef.current = currentOptionChoicesRef.current;
      latestSentSelectedReportingUnitIdRef.current = currentSelectedReportingUnitIdRef.current;
      latestSentActiveOptionChoiceRef.current = { optionSetId, optionId };
      latestRequestWasOptionIntentRef.current = true;
      pendingIntentReflectionRef.current = {
        baseOptionChoices: currentOptionChoicesRef.current,
        resolvedOptionChoices: null,
      };
      generationInFlightRef.current = true;
      generationStartTimeRef.current = generationStartTime;
      resetShowroomMaterialWarmup(sessionId);
      setFloorPlanError(false);
      setIsGenerating(true);
      setError(null);
      armWorkerWatchdog({ sessionId, requestId, phase: "applyOptionChoice" });
      return { status: "posted", requestId };
    },
    [armWorkerWatchdog, postMessageToWorker],
  );

  // Subscribe to worker messages while the hook is enabled.
  useEffect(() => {
    if (!effectivelyEnabled) {
      return;
    }

    const singletonState = getShowroomWorkerSingletonState();
    const bufferedFailure = singletonState.failure;
    if (bufferedFailure) {
      let cancelled = false;
      queueMicrotask(() => {
        if (!cancelled) {
          handleTerminalFailure(bufferedFailure.error, bufferedFailure.phase, {
            worker: singletonState.worker,
            sessionId: bufferedFailure.sessionId,
            metadata: {
              fallbackMessage: bufferedFailure.fallbackMessage,
              filename: bufferedFailure.filename,
              lineno: bufferedFailure.lineno,
              colno: bufferedFailure.colno,
            },
          });
        }
      });
      return () => {
        cancelled = true;
      };
    }

    const worker = getOrCreateShowroomWorker();
    if (!worker) {
      const startupFailure =
        singletonState.failure ??
        recordTerminalShowroomWorkerFailure(undefined, "startup", undefined, {
          fallbackMessage: "Failed to start showroom worker",
        });
      let cancelled = false;
      queueMicrotask(() => {
        if (!cancelled) {
          handleTerminalFailure(startupFailure.error, startupFailure.phase, {
            worker: null,
            sessionId: startupFailure.sessionId,
            metadata: {
              fallbackMessage: startupFailure.fallbackMessage,
              filename: startupFailure.filename,
              lineno: startupFailure.lineno,
              colno: startupFailure.colno,
            },
          });
        }
      });
      return () => {
        cancelled = true;
      };
    }

    setShowroomWorkerObserved("active");
    workerRef.current = worker;

    const handleMessage = (event: MessageEvent<ShowroomBuildingWorkerToMainMessage>) => {
      const message = event.data;

      // Discard messages from stale sessions.
      if (message.sessionId !== sessionIdRef.current) {
        return;
      }
      if (message.requestId !== latestWorkerRequestIdRef.current) {
        return;
      }

      switch (message.type) {
        case "initialized": {
          refreshWorkerWatchdog(message.requestId);
          const initializedReceivedTime = performance.now();
          recordClientBenchmarkPhase(
            "worker.client.initializePostToInitialized",
            initializePostTimeRef.current,
            initializedReceivedTime,
          );
          initializedReceivedTimeRef.current = initializedReceivedTime;
          setPlanData(message.planData);
          setError(null);
          break;
        }

        case "optionStateResolved": {
          refreshWorkerWatchdog(message.requestId);
          latestResolvedChoicesRef.current = message.resolvedOptionChoices;
          latestSentChoicesRef.current = message.resolvedOptionChoices;
          optionIntentReadyRef.current = initializedOptionStateProtocolRef.current === "optionStateV2";
          if (latestRequestWasOptionIntentRef.current && pendingIntentReflectionRef.current) {
            pendingIntentReflectionRef.current.resolvedOptionChoices = message.resolvedOptionChoices;
          } else {
            pendingIntentReflectionRef.current = {
              baseOptionChoices: message.requestedOptionChoices,
              resolvedOptionChoices: message.resolvedOptionChoices,
            };
          }
          break;
        }

        case "catalogResolved": {
          refreshWorkerWatchdog(message.requestId);
          latestResolvedChoicesRef.current = message.resolvedOptionChoices;
          latestSentChoicesRef.current = message.resolvedOptionChoices;
          if (latestRequestWasOptionIntentRef.current && pendingIntentReflectionRef.current) {
            pendingIntentReflectionRef.current.resolvedOptionChoices = message.resolvedOptionChoices;
          }
          const reconciledSnapshot = reconcileShowroomOptionCatalogSnapshot(
            optionCatalogSnapshotRef.current,
            optionCatalogFingerprintsRef.current,
            message.snapshot,
            message.optionSetFingerprints,
          );
          optionCatalogSnapshotRef.current = reconciledSnapshot;
          optionCatalogFingerprintsRef.current = message.optionSetFingerprints;
          startTransition(() => setOptionCatalogSnapshot(reconciledSnapshot));
          break;
        }

        case "materialWarmup":
          recordClientBenchmarkPhase(
            message.final
              ? "worker.client.updateOptionsPostToMaterialWarmup"
              : "worker.client.updateOptionsPostToEarlyManifest",
            generationStartTimeRef.current,
            performance.now(),
          );
          publishShowroomMaterialWarmup({
            sessionId: message.sessionId,
            materialSources: message.materialSources,
            designMaterialParamsByKey: message.designMaterialParamsByKey,
            final: message.final,
          });
          if (message.propAssetIds?.length) {
            prefetchShowroomPropAssets(message.propAssetIds);
          }
          break;

        case "updated":
          refreshWorkerWatchdog(message.requestId, "floorPlan");
          const updatedReceivedTime = performance.now();
          recordClientBenchmarkPhase(
            "worker.client.updateOptionsPostToUpdated",
            generationStartTimeRef.current,
            updatedReceivedTime,
          );
          if (message.timing.sourceResponseTotal?.startTimeMs !== undefined) {
            recordClientBenchmarkPhase(
              "worker.client.sourceResponseEndToUpdated",
              message.timing.sourceResponseTotal.startTimeMs +
                message.timing.sourceResponseTotal.durationMs -
                performance.timeOrigin,
              updatedReceivedTime,
            );
          }
          recordWorkerSourceTiming(message.timing);
          const clientObservedTotal =
            generationStartTimeRef.current !== null
              ? {
                  startTimeMs: performance.timeOrigin + generationStartTimeRef.current,
                  durationMs: updatedReceivedTime - generationStartTimeRef.current,
                }
              : { durationMs: message.timing.sourcePipelineTotal.durationMs };
          setTiming({ ...message.timing, clientObservedTotal });
          setRequestedOptionChoices(message.requestedOptionChoices);
          setRenderPayload(message.renderPayload);
          setIsGenerating(false);
          generationInFlightRef.current = false;
          latestRequestWasOptionIntentRef.current = false;
          // The refreshed floor plan follows in a separate `floorPlan` message; clear the
          // previous generation's payload so the 2D view never shows a stale plan.
          setFloorPlan(null);
          setError(null);
          showroomPerformance((perf) => {
            perf.recordTiming("workerBuildingGeneration", message.timing.buildingGeneration);
            perf.recordTiming("workerGeometryCollection", message.timing.geometryCollection);
            perf.recordTiming("workerRenderPayloadAssembly", message.timing.renderPayloadAssembly);
          });
          generationStartTimeRef.current = null;
          break;

        case "floorPlan":
          refreshWorkerWatchdog(message.requestId);
          recordShowroomIntroBenchmarkPhaseFromTiming({
            name: "worker.source.floorPlanGeneration",
            source: "worker",
            ...message.timing,
          });
          setFloorPlan(message.floorPlan);
          setFloorPlanError(false);
          break;

        case "error":
          console.error("ShowroomWorkerBuilding: Error:", message.error.message);
          if (message.error.phase === "floorPlan") {
            handleFloorPlanFailure(message.error, message.sessionId);
            break;
          }
          handleTerminalFailure(message.error, message.error.phase, { worker, sessionId: message.sessionId });
          break;

        case "heartbeat":
          refreshWorkerWatchdog(message.requestId);
          break;

        case "idle":
          if (workerWatchdogRef.current?.requestId === message.requestId) {
            stopWorkerWatchdog();
          }
          break;
      }
    };

    const handleError = (event: ErrorEvent) => {
      event.preventDefault();
      console.error("ShowroomWorkerBuilding: Worker error:", event.message || "Worker error");
      handleTerminalFailure(event.error, "runtime", {
        worker,
        sessionId: sessionIdRef.current,
        metadata: {
          fallbackMessage: event.message || "Worker error",
          filename: event.filename || undefined,
          lineno: event.lineno || undefined,
          colno: event.colno || undefined,
        },
      });
    };

    const handleMessageError = () => {
      const messageError = new Error("Failed to deserialize a showroom worker message");
      handleTerminalFailure(messageError, "messageerror", { worker, sessionId: sessionIdRef.current });
    };

    worker.addEventListener("message", handleMessage);
    worker.addEventListener("error", handleError);
    worker.addEventListener("messageerror", handleMessageError);

    return () => {
      // Keep the singleton alive across re-mounts; just detach this subscription
      // so stale callbacks don't fire and discarded sessions don't leak handlers.
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
      worker.removeEventListener("messageerror", handleMessageError);
      stopWorkerWatchdog();
      setShowroomWorkerObserved("detached");
      workerRef.current = null;
    };
  }, [
    effectivelyEnabled,
    handleFloorPlanFailure,
    handleTerminalFailure,
    refreshWorkerWatchdog,
    sendUpdateOptions,
    stopWorkerWatchdog,
  ]);

  // Open / re-open a session whenever the identifier key changes.
  useEffect(() => {
    if (!effectivelyEnabled || !sourceKey) {
      stopWorkerWatchdog();
      // Reset local state when going from enabled -> disabled or identifier-cleared.
      if (sessionIdRef.current && workerRef.current) {
        if (
          !postMessageToWorker(
            workerRef.current,
            { type: "reset", sessionId: sessionIdRef.current },
            "resetPostMessage",
          )
        ) {
          return;
        }
      }
      resetShowroomMaterialWarmup(sessionIdRef.current ?? undefined);
      sessionIdRef.current = null;
      latestWorkerRequestIdRef.current = null;
      latestSentChoicesRef.current = null;
      latestSentSelectedReportingUnitIdRef.current = undefined;
      latestSentActiveOptionChoiceRef.current = undefined;
      latestResolvedChoicesRef.current = null;
      initializedOptionStateProtocolRef.current = null;
      optionIntentReadyRef.current = false;
      generationInFlightRef.current = false;
      latestRequestWasOptionIntentRef.current = false;
      pendingIntentReflectionRef.current = null;
      optionCatalogSnapshotRef.current = null;
      optionCatalogFingerprintsRef.current = null;
      lastIdentifierKeyRef.current = null;
      initializePostTimeRef.current = null;
      initializedReceivedTimeRef.current = null;
      generationStartTimeRef.current = null;
      setRenderPayload(null);
      setRequestedOptionChoices(null);
      setPlanData(null);
      setFloorPlan(null);
      setFloorPlanError(false);
      setIsGenerating(false);
      setError(null);
      setTiming(null);
      setOptionCatalogSnapshot(null);
      return;
    }

    if (getShowroomWorkerSingletonState().failure) {
      return;
    }

    if (lastIdentifierKeyRef.current === sourceKey) {
      return;
    }

    const worker = workerRef.current;
    if (!worker) {
      return;
    }

    // Tear down the previous session.
    if (sessionIdRef.current) {
      stopWorkerWatchdog();
      if (!postMessageToWorker(worker, { type: "reset", sessionId: sessionIdRef.current }, "resetPostMessage")) {
        return;
      }
      resetShowroomMaterialWarmup(sessionIdRef.current);
    }

    const sessionId = generateSessionId();
    const requestId = generateRequestId();
    sessionIdRef.current = null;
    latestWorkerRequestIdRef.current = null;
    lastIdentifierKeyRef.current = null;
    latestSentChoicesRef.current = null;
    latestSentSelectedReportingUnitIdRef.current = undefined;
    latestSentActiveOptionChoiceRef.current = undefined;
    latestResolvedChoicesRef.current = null;
    initializedOptionStateProtocolRef.current = null;
    optionIntentReadyRef.current = false;
    generationInFlightRef.current = true;
    latestRequestWasOptionIntentRef.current = false;
    pendingIntentReflectionRef.current = null;
    optionCatalogSnapshotRef.current = null;
    optionCatalogFingerprintsRef.current = null;
    initializePostTimeRef.current = null;
    initializedReceivedTimeRef.current = null;
    generationStartTimeRef.current = null;

    // Reset per-session UI state.
    setRenderPayload(null);
    setRequestedOptionChoices(null);
    setPlanData(null);
    setFloorPlan(null);
    setFloorPlanError(false);
    setIsGenerating(true);
    setError(null);
    setTiming(null);
    setOptionCatalogSnapshot(null);

    let message: MainToShowroomBuildingWorkerMessage;
    if (planDataOverride) {
      message = {
        type: "initialize",
        sessionId,
        requestId,
        planData: planDataOverride,
        enabledFeatures,
        optionStateProtocol,
        catalogConfig: optionCatalogConfig,
      };
    } else {
      assertIsNotNullOrUndefined(identifiers);
      message = {
        type: "initialize",
        sessionId,
        requestId,
        identifiers,
        enabledFeatures,
        optionStateProtocol,
        catalogConfig: optionCatalogConfig,
      };
    }
    const initializePostTime = performance.now();
    if (!postMessageToWorker(worker, message, "initializePostMessage")) {
      return;
    }

    sessionIdRef.current = sessionId;
    latestWorkerRequestIdRef.current = requestId;
    lastIdentifierKeyRef.current = sourceKey;
    initializedOptionStateProtocolRef.current = optionStateProtocol;
    initializePostTimeRef.current = initializePostTime;
    armWorkerWatchdog({ sessionId, requestId, phase: "initialize" });
  }, [
    armWorkerWatchdog,
    effectivelyEnabled,
    enabledFeatures,
    identifiers,
    optionCatalogConfig,
    planDataOverride,
    optionStateProtocol,
    postMessageToWorker,
    sourceKey,
    stopWorkerWatchdog,
  ]);

  // Push option-choice changes within an active session.
  useEffect(() => {
    if (!effectivelyEnabled || !optionChoicesReady || !planData || !sessionIdRef.current || !workerRef.current) {
      return;
    }

    const hasNewActiveChoice =
      activeOptionChoice !== undefined && latestSentActiveOptionChoiceRef.current !== activeOptionChoice;
    const requestContextIsUnchanged =
      latestSentSelectedReportingUnitIdRef.current === selectedReportingUnitId && !hasNewActiveChoice;
    const pendingIntentReflection = pendingIntentReflectionRef.current;
    if (pendingIntentReflection) {
      if (shallowCompare(pendingIntentReflection.baseOptionChoices, optionChoices) && requestContextIsUnchanged) {
        return;
      }
      if (
        pendingIntentReflection.resolvedOptionChoices &&
        shallowCompare(pendingIntentReflection.resolvedOptionChoices, optionChoices) &&
        requestContextIsUnchanged
      ) {
        latestSentChoicesRef.current = pendingIntentReflection.resolvedOptionChoices;
        pendingIntentReflectionRef.current = null;
        return;
      }
      pendingIntentReflectionRef.current = null;
    }

    if (
      shallowCompare(latestSentChoicesRef.current, optionChoices) &&
      latestSentSelectedReportingUnitIdRef.current === selectedReportingUnitId &&
      !hasNewActiveChoice
    ) {
      return;
    }

    // Only the first option post measures "init -> first options post". initializedReceivedTimeRef
    // is not reset between updates, so recording it on later sends would span the original init to
    // the current send.
    if (latestSentChoicesRef.current === null) {
      recordClientBenchmarkPhase(
        "worker.client.initializedToUpdateOptionsPost",
        initializedReceivedTimeRef.current,
        performance.now(),
      );
    }
    sendUpdateOptions(optionChoices, selectedReportingUnitId, activeOptionChoice);
  }, [
    activeOptionChoice,
    effectivelyEnabled,
    optionChoices,
    optionChoicesReady,
    planData,
    selectedReportingUnitId,
    sendUpdateOptions,
  ]);

  return useMemo(
    () => ({
      renderPayload,
      requestedOptionChoices,
      planData,
      floorPlan,
      floorPlanError,
      isGenerating,
      error,
      timing,
      optionCatalogSnapshot,
      dispatchOptionIntent,
      reportRenderPayloadHydrationFailure,
    }),
    [
      error,
      dispatchOptionIntent,
      floorPlan,
      floorPlanError,
      isGenerating,
      optionCatalogSnapshot,
      planData,
      renderPayload,
      reportRenderPayloadHydrationFailure,
      requestedOptionChoices,
      timing,
    ],
  );
}
